import {
  AlignType,
  ETextStyleVariant,
  RadioSelector,
  SelectorGroup,
  Text,
} from '@outdoorsyco/bonfire';
import React from 'react';

import { EGuidedSearchComponentType, TGuidedSearchComponentField } from './types';

type TGuidedSearchComponentFieldProps = TGuidedSearchComponentField & {
  value?: string | string[];
  onChange: (value?: string | string[]) => void;
  id: string;
};

export const GuidedSearchComponentField = ({
  component_type,
  properties: { rows, title, description },
  value,
  onChange,
  id,
}: TGuidedSearchComponentFieldProps) => {
  if (component_type === EGuidedSearchComponentType.ButtonGroupRadio) {
    // Check if this is the "where are you planning on traveling" question using the field ID
    const isLocationQuestion = id === 'where-are-you-traveling';

    // CSS styles for horizontal radio layout - responsive version
    const horizontalRadioStyles = `
      /* Mobile: keep vertical (default behavior) */
      .horizontal-radio-group [role=radiogroup] > div {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }
      
      /* Tablet and up: horizontal layout */
      @media (min-width: 768px) {
        .horizontal-radio-group [role=radiogroup] > div {
          display: flex !important;
          flex-direction: row !important;
          gap: 1rem;
        }
        .horizontal-radio-group [role=radiogroup] > div > div {
          flex: 1;
        }
      }
    `;

    if (isLocationQuestion) {
      // CSS styles for hybrid grid layout - only for "Where are you planning on traveling?" question
      const hybridGridStyles = `
        /* Mobile: keep vertical (default behavior) */
        .hybrid-grid-group [role=radiogroup] > div {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        
        /* Tablet and up: hybrid grid layout */
        @media (min-width: 768px) {
          .hybrid-grid-group [role=radiogroup] > div {
            display: grid !important;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
          }
          .hybrid-grid-group [role=radiogroup] > div > div:last-child {
            grid-column: 1 / -1;
          }
        }
      `;

      return (
        <>
          <style dangerouslySetInnerHTML={{ __html: hybridGridStyles }} />
          <FieldWrapper title={title}>
            <div className="hybrid-grid-group">
              <SelectorGroup
                ariaLabel={description}
                value={(value as string) || ''}
                onChange={onChange as (value: string) => void}>
                {rows.map(row => {
                  return (
                    <RadioSelector
                      key={row.id}
                      name={row.id}
                      value={row.id}
                      ariaLabel={row.label}
                      label={row.label}
                      alignVariant={AlignType.VerticalCenter}
                      showIndicator={false}
                      withBorder
                    />
                  );
                })}
              </SelectorGroup>
            </div>
          </FieldWrapper>
        </>
      );
    }

    // For non-location questions, use horizontal layout like Driver's age modal
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: horizontalRadioStyles }} />
        <FieldWrapper title={title}>
          <div className="horizontal-radio-group">
            <SelectorGroup
              ariaLabel={description}
              value={(value as string) || ''}
              onChange={onChange as (value: string) => void}>
              {rows.map(row => {
                return (
                  <RadioSelector
                    key={row.id}
                    name={row.id}
                    value={row.id}
                    ariaLabel={row.label}
                    label={row.label}
                    alignVariant={AlignType.VerticalCenter}
                    showIndicator={false}
                    withBorder
                  />
                );
              })}
            </SelectorGroup>
          </div>
        </FieldWrapper>
      </>
    );
  }

  throw new Error('Unsupported component type!');
};

type TFieldWrapperProps = {
  title?: string;
  children: JSX.Element;
};

const FieldWrapper = ({ title, children }: TFieldWrapperProps) => {
  return (
    <div className="flex flex-col gap-3">
      {title && <Text variant={ETextStyleVariant.SmallRegular}>{title}</Text>}
      {children}
    </div>
  );
};
